// Libraries
import React, { Component } from "react";
import { Link } from "react-router-dom";
import Modal from "react-modal";
import _ from "lodash";

// Child Components
import MapView from "../../common/map/mapView";
import Search from "../../common/search";
import Language from "../../common/misc/language";
import Pagination from "../../common/pagination";
import ListGroup from "../../common/misc/listGroup";
import Expire from "../../common/expire";
import Cards from "./cards";
import * as utils from "../../common/misc/utils";

// Functions
import { paginate } from "../../../utils/scripts/paginate";
import { getEntities } from "../../../services/apiService";

// Modal Component
var appElement = document.getElementById("root");
Modal.setAppElement(appElement);

// Constants
const baseUrl = process.env.REACT_APP_API_URL;

// Helper function for efficient audio/video HTML tag creation
function mediaHtmlTag(selectedPoi, item) {
  return item.medianame.toLowerCase().endsWith(".mp3") ? (
    <audio
      src={`${baseUrl}/pois/${selectedPoi._id}/${item.medianame}`}
      className="w-100 mt-3 audio-tag rounded"
      controls={true}
    />
  ) : (
    <video
      src={`${baseUrl}/pois/${selectedPoi._id}/${item.medianame}`}
      style={{ maxHeight: "640px" }}
      controls={true}
    />
  );
}

class Gallery extends Component {
  state = {
    pois: [],
    selectedPoi: null,
    searchQuery: "",
    selectedCategory: null,
    selectedTags: [],
    language: "de",
    currentPage: 1,
    initialSize: 3,
    pageSize: 3,
    sortColumn: { path: "priority", order: "asc" },

    views: [
      { id: "grid", name: "Grid" },
      { id: "map", name: "Map" },
    ],
    selectedView: { id: "grid" },

    queryParams: null,
    hideViews: false,
    hideSearch: false,
    noSelTC: false,
  };

  // Retrive the Data from the Server
  async componentDidMount() {
    // Get Data
    const { data: pois } = await getEntities("v2/gallery");
    if (pois.length === 0) return;

    // Page Query
    const queryParams = new URLSearchParams(window.location.search);
    const currentPageQuery = queryParams.get("currentPage");
    const pageSizeQuery = queryParams.get("pageSize");
    const languageQuery = queryParams.get("lang");
    const viewQuery = queryParams.get("view");
    const poiQuery = queryParams.get("id");
    const categoryFilter = queryParams.get("category");
    const tagsFilter = queryParams.get("tags");
    const hideViews = utils.booleanQuery(queryParams.get("noViews"));
    const hideSearch = utils.booleanQuery(queryParams.get("noSearch"));
    const noSelTC = utils.booleanQuery(queryParams.get("noSelTC"));

    // Initialize state
    this.setState({ pois, queryParams, hideViews, hideSearch, noSelTC });

    // Query Event Handler
    this.handlePageLoad(
      currentPageQuery,
      pageSizeQuery,
      languageQuery,
      viewQuery,
      poiQuery,
      categoryFilter,
      tagsFilter
    );
  }

  // Gallery Page loading Event Handler
  handlePageLoad = (
    currentPageQuery,
    pageSizeQuery,
    languageQuery,
    viewQuery,
    poiQuery,
    categoryFilter,
    tagsFilter
  ) => {
    if (currentPageQuery && pageSizeQuery) {
      this.setState({
        currentPage: parseInt(currentPageQuery),
        pageSize: parseInt(pageSizeQuery),
        initialSize: parseInt(pageSizeQuery),
      });
    }

    if (languageQuery) {
      this.setState({
        language: languageQuery,
      });
    }

    if (viewQuery) {
      const { pois, initialSize } = this.state;
      const currentSize = viewQuery === "map" ? pois.length : initialSize;

      this.setState({
        selectedView: { id: viewQuery },
        searchQuery: "",
        currentPage: 1,
        pageSize: currentSize,
      });
    }

    if (poiQuery) {
      const selectedPoi = this.state.pois.find((p) => p._id === poiQuery);

      if (selectedPoi) this.handleViewPoi(selectedPoi);
    }

    if (categoryFilter) {
      this.setState({
        searchQuery: "",
        selectedCategory: { _id: categoryFilter },
        currentPage: 1,
      });
    }

    if (tagsFilter) {
      this.setState({
        searchQuery: "",
        selectedTags: tagsFilter.split(","),
        currentPage: 1,
      });
    }
  };

  // Pagination Event Handler
  handlePageChange = (page) => {
    this.setState({ currentPage: page });
  };

  // PageSize Event Handler
  handleSizeChange = ({ currentTarget: input }) => {
    let pageSize = { ...this.state.pageSize };
    pageSize = input.value;

    this.setState({ pageSize });
  };

  // View Poi Clicked
  handleViewPoi = (selectedPoi) => {
    this.setState({
      selectedPoi: selectedPoi,
    });

    // Open Modal
    this.openModal();
  };

  // Search Event Handler
  handleSearch = (query) => {
    this.setState({
      searchQuery: query,
      selectedCategory: null,
      currentPage: 1,
    });
  };

  // Sort Event Handler
  handleSort = (sortColumn) => {
    this.setState({ sortColumn });
  };

  // Get state Data
  getData = () => {
    const {
      currentPage,
      pageSize,
      pois: allPois,
      selectedCategory,
      selectedTags,
      searchQuery,
      sortColumn,
    } = this.state;

    // Calculate StartNumber
    // const startNumber = (currentPage - 1) * pageSize;

    // Get Filter Options
    let filtered = [];
    if (searchQuery)
      filtered = allPois.filter(
        (p) =>
          p.title_en.toLowerCase().indexOf(searchQuery.toLowerCase()) !== -1 ||
          p.title_de.toLowerCase().indexOf(searchQuery.toLowerCase()) !== -1
      );
    else if (selectedCategory && selectedCategory._id)
      filtered = allPois.filter((p) => p.category._id === selectedCategory._id);
    else if (selectedTags && selectedTags.length > 0)
      filtered = allPois.filter((p) =>
        p.tags.some((poiTag) =>
          selectedTags.some((selTagId) => selTagId === poiTag._id)
        )
      );
    else filtered = allPois;

    // Define Sorting Option
    const sorted = _.orderBy(filtered, [sortColumn.path], [sortColumn.order]);

    // Define Pagination
    const pois = paginate(sorted, currentPage, pageSize);

    return {
      // startNumber: startNumber,
      totalCount: filtered.length,
      data: pois,
    };
  };

  // Open Modal Function
  openModal = () => {
    this.setState({ modalIsOpen: true });
  };

  // Close Modal Function
  closeModal = () => {
    this.setState({ modalIsOpen: false });
  };

  // Modal Close Event Handler
  handleModalCloseRequest = () => {
    const { selectedPoi, queryParams } = this.state;

    // Update query params to not include the id parameter in case we're closing a modal detail
    // view of a POI
    const queryParamsWithoutPoi = selectedPoi
      ? // Special handling in case we're detail-viewing a POI, in which case we will want
        // to eliminate any id parameter from the saved query, otherwise the close and back
        // buttons would just lead to detail-viewing that POI instead.
        utils.selectQueriesExcept(queryParams, ["id"])
      : queryParams;

    this.setState({
      modalIsOpen: false,
      selectedPoi: null,
      queryParams: queryParamsWithoutPoi,
    });
  };

  handleViewSelect = (view) => {
    const { pois, initialSize, queryParams } = this.state;
    const currentSize = view.id === "map" ? pois.length : initialSize;

    const filteredParams = utils.selectQueriesExcept(queryParams, ["view"]);
    window.location.href =
      `/gallery/?view=${view.id}` +
      utils.smartPrepend("&", filteredParams.toString());

    this.setState({
      selectedView: view,
      searchQuery: "",
      currentPage: 1,
      pageSize: currentSize,
    });
  };

  handleLanguageSelect = ({ currentTarget: input }) => {
    const selectedLanguage = input.value;

    const filteredParams = utils.selectQueriesExcept(this.state.queryParams, [
      "lang",
    ]);
    window.location.href =
      `/gallery/?lang=${selectedLanguage}` +
      utils.smartPrepend("&", filteredParams.toString());

    this.setState({ language: selectedLanguage });
  };

  render() {
    const {
      currentPage,
      pageSize,
      searchQuery,
      // selectedCategory,
      // selectedTags,
      selectedPoi,
      language,
      selectedView,
      queryParams,
      hideViews,
      hideSearch,
      noSelTC,
    } = this.state;

    // Update view selection labels according to language
    const views =
      language === "de"
        ? [
            { id: "grid", name: "Liste" },
            { id: "map", name: "Karte" },
          ]
        : this.state.views;

    const { totalCount, data: pois } = this.getData();

    // Setup query parameter forwarding
    const savedQueryParamsString = (() => {
      if (!queryParams) return "";
      if (selectedPoi) {
        // Special handling in case we're detail-viewing a POI, in which case we will want
        // to eliminate any id parameter from the saved query, otherwise the close and back
        // buttons would just lead to detail-viewing that POI instead.
        return utils.selectQueriesExcept(queryParams, ["id"]).toString();
      } else return queryParams.toString();
    })();

    return (
      <div className="col mt-3">
        <div className="row justify-content-between align-items-center">
          {!hideViews && (
            <div className="col-md-6 my-1">
              <ListGroup
                items={views}
                selectedItem={selectedView}
                onItemSelect={this.handleViewSelect}
              />
            </div>
          )}
          {!hideSearch && (
            <div className="col-sm-12 col-md-5 col-lg-4 col-xl-3 my-1 text-end">
              <Search
                value={searchQuery}
                onChange={this.handleSearch}
                language={language}
              />
            </div>
          )}
        </div>

        {!(hideViews && hideSearch) && <hr />}

        {pois.length !== 0 && selectedView.id === "grid" && (
          <Cards
            pois={pois}
            lang={language}
            onViewClick={this.handleViewPoi}
            queryParams={queryParams}
            noSelTC={noSelTC}
          />
        )}
        {pois.length !== 0 && selectedView.id === "map" && (
          <MapView
            style={{ height: "80vh", width: "100%" }}
            pois={pois}
            lang={language}
            queryParams={queryParams}
            noSelTC={noSelTC}
          />
        )}

        <Modal
          className="Modal__Bootstrap modal-dialog"
          closeTimeoutMS={150}
          isOpen={this.state.modalIsOpen}
          onRequestClose={this.handleModalCloseRequest}
        >
          {selectedPoi ? (
            <div
              className={
                selectedPoi ? "modal-content" : "modal-content vertical-content"
              }
            >
              <div className="modal-header">
                {selectedPoi ? (
                  <h5 className="modal-title modal-title-text">
                    <strong>
                      {language === "de"
                        ? selectedPoi.title_de
                        : selectedPoi.title_en}
                    </strong>
                  </h5>
                ) : null}

                <Link
                  type="button"
                  className="close"
                  onClick={this.handleModalCloseRequest}
                  to={
                    "/gallery/" +
                    utils.smartPrepend("?", savedQueryParamsString)
                  }
                >
                  <span aria-hidden="true">
                    <i className="bx bx-x bx-md"></i>
                  </span>
                  <span className="sr-only">Close</span>
                </Link>
              </div>

              <div className="modal-body">
                {selectedPoi ? (
                  <div className="row justify-content-center">
                    <div className="col-md-10 col-sm-12">
                      <img
                        className="image-preview"
                        src={`${baseUrl}/pois/${selectedPoi._id}/${selectedPoi.filename}`}
                        alt={selectedPoi.title_en}
                      />

                      <p className="modal-caption mt-2">
                        <small>
                          {language === "de"
                            ? selectedPoi.caption_de
                            : selectedPoi.caption_en}
                        </small>
                      </p>

                      <p
                        className="text-justify mt-3"
                        style={{ color: "white" }}
                      >
                        {language === "de"
                          ? selectedPoi.description_de
                          : selectedPoi.description_en}
                      </p>
                    </div>
                  </div>
                ) : null}

                {selectedPoi.items.map((item) => (
                  <div
                    className="row justify-content-center mt-3"
                    key={item._id ? item._id : selectedPoi._id}
                  >
                    <div className="col-md-10 col-sm-12">
                      <img
                        className="image-preview"
                        src={`${baseUrl}/pois/${selectedPoi._id}/${item.filename}`}
                        alt={
                          language === "de" ? item.caption_de : item.caption_en
                        }
                      />
                      {item.has_media ? mediaHtmlTag(selectedPoi, item) : null}

                      <p className="modal-caption mt-2">
                        <small>
                          {language === "de"
                            ? item.caption_de
                            : item.caption_en}
                        </small>
                      </p>

                      <p
                        className="text-justify mt-3"
                        style={{ color: "white" }}
                      >
                        {language === "de"
                          ? item.description_de
                          : item.description_en}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="modal-footer">
                <Link
                  type="button"
                  className="btn btn-secondary rounded"
                  onClick={this.handleModalCloseRequest}
                  to={
                    "/gallery/" +
                    utils.smartPrepend("?", savedQueryParamsString)
                  }
                >
                  {language === "de" ? "Zurück zur Galerie" : "Back to Gallery"}
                </Link>
              </div>
            </div>
          ) : null}
        </Modal>

        {pois.length === 0 && (
          <div className="row">
            <div className="col-sm-12 align-self-center">
              <Expire delay={5000}>
                <div className="loader">
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
              </Expire>
            </div>
          </div>
        )}

        <hr />

        <div className="row align-items-center">
          <div className="col-sm-12 col-md col-lg col-xl mr-auto ">
            <Pagination
              itemsCount={totalCount}
              pageSize={pageSize}
              currentPage={currentPage}
              onPageChange={this.handlePageChange}
            />
          </div>
          <div className="col-auto align-middle">
            <Language
              onChange={this.handleLanguageSelect}
              selectedLang={language}
            />
          </div>
        </div>
      </div>
    );
  }
}

export default Gallery;
